import type {
  BusinessUnitNodeRecord,
  BusinessUnitWritePayload,
  BusinessUnitRecord,
  DepartmentNodeRecord,
  DepartmentRecord,
  DepartmentWritePayload,
  DivisionNodeRecord,
  DivisionRecord,
  DivisionWritePayload,
  LegalEntityNodeRecord,
  LegalEntityRecord,
  LegalEntityWritePayload,
  OrganizationTreeRecord,
} from '../../models';
import { financialYearDates } from '@/shared/utils/financialYear';

/**
 * Mock backend for step 4, shaped to the real `GET /organizations/{code}/tree`.
 *
 * Stateful, like steps 1 and 2 and unlike step 3: the step writes one node per dialog Save, so the mock
 * has to remember them or the tree would snap back on every reload and the add/edit/delete flows could
 * not be exercised at all.
 *
 * It starts from the reference's two entities -- one with a full Business Unit → Division → Department
 * chain, one with none -- because the second is what makes the "a level with no children" and
 * "delete is available on a leaf only" cases visible without any setup.
 *
 * No level holds a location: mapping one is out of scope (design decision 16).
 */

const ORGANIZATION_ID = 1;
const ORGANIZATION_NAME = 'Niraxo Technologies';

/** Ids are handed out from one counter across all four levels, as distinct rows in one session. */
let nextId = 100;

function issueId(): number {
  nextId += 1;
  return nextId;
}

function seedTree(organizationCode: string): OrganizationTreeRecord {
  return {
    id: ORGANIZATION_ID,
    organizationCode,
    organizationName: ORGANIZATION_NAME,
    statusId: 10,
    statusName: 'Pending',
    legalEntities: [
      {
        id: 11,
        entityName: 'Niraxo India',
        entityCode: 'ENT-001',
        statusName: 'Active',
        businessUnits: [
          {
            id: 21,
            name: 'Manufacturing',
            businessUnitCode: 'BU-001',
            statusId: 11,
            statusName: 'Active',
            divisions: [
              {
                id: 31,
                name: 'Engineering',
                divisionCode: 'DIV-001',
                departments: [
                  { id: 41, name: 'Platform', departmentCode: 'DEP-001' },
                  { id: 42, name: 'Mobile', departmentCode: 'DEP-002' },
                ],
              },
            ],
          },
        ],
      },
      // Deliberately childless: the empty-children path has to be renderable without setting it up.
      {
        id: 12,
        entityName: 'Niraxo Singapore',
        entityCode: 'ENT-002',
        statusName: 'Inactive',
        businessUnits: [],
      },
    ],
  };
}

let tree: OrganizationTreeRecord | null = null;

function current(organizationCode: string): OrganizationTreeRecord {
  if (!tree || tree.organizationCode !== organizationCode) {
    tree = seedTree(organizationCode);
  }
  return tree;
}

export function mockOrganizationTree(organizationCode: string): OrganizationTreeRecord {
  return structuredClone(current(organizationCode));
}

/** Resets the session's tree — for tests that need the seed back. */
export function resetMockHierarchy(): void {
  tree = null;
  nextId = 100;
  extras = new Map();
}

/* -- lookups --------------------------------------------------------------- */

function entities(): LegalEntityNodeRecord[] {
  return tree?.legalEntities ?? [];
}

function businessUnits(): BusinessUnitNodeRecord[] {
  return entities().flatMap((entity) => entity.businessUnits);
}

function divisions(): DivisionNodeRecord[] {
  return businessUnits().flatMap((unit) => unit.divisions);
}

function departments(): DepartmentNodeRecord[] {
  return divisions().flatMap((division) => division.departments);
}

/* -- writes ---------------------------------------------------------------- */

/*
  One function per level rather than one switch over a node type. The four
  endpoints take four different bodies and return four different rows, so a
  single generic signature would have to widen the return type and push a cast
  into the service -- which is exactly the type safety the service exists to
  give its callers.
*/

function notFound(what: string, id: number): Error {
  return new Error(`Mock hierarchy has no ${what} ${String(id)}`);
}

/*
  -- the fields the tree does not carry --------------------------------------

  The real tree endpoint returns only an id, a name and a code per row, while
  every level's GET returns the whole record. The mock has to reproduce that
  split or the edit dialog would appear to work here and blank real data
  against the server: the tree is the only thing it reads from.

  Kept beside the tree rather than inside its nodes, so a node stays exactly
  the shape the tree endpoint sends.
*/
interface ExtraFields {
  integratedCode?: string;
  description?: string;
  financialYearStartDate?: string;
  financialYearEndDate?: string;
  entityLogo?: string;
  entityLogoName?: string | null;
}

let extras = new Map<string, ExtraFields>();

const extraKey = (level: string, id: number): string => `${level}:${String(id)}`;

function seedExtras(): Map<string, ExtraFields> {
  return new Map<string, ExtraFields>([
    [
      extraKey('entity', 11),
      {
        integratedCode: 'INT-ENT-001',
        description: 'The India entity.',
        financialYearStartDate: '2026-04-01',
        financialYearEndDate: '2027-03-31',
      },
    ],
    [
      extraKey('entity', 12),
      {
        financialYearStartDate: '2026-04-01',
        financialYearEndDate: '2027-03-31',
      },
    ],
    [extraKey('businessUnit', 21), { integratedCode: 'INT-BU-001', description: 'Head office.' }],
    [extraKey('division', 31), { integratedCode: 'INT-DIV-001' }],
    [extraKey('department', 41), { description: 'The platform team.' }],
  ]);
}

function extrasFor(level: string, id: number): ExtraFields {
  if (extras.size === 0) extras = seedExtras();
  return extras.get(extraKey(level, id)) ?? {};
}

/**
 * The two dates the server would store for the months a write sends -- the API takes months and answers
 * dates, so the mock derives them by the same rule (see `financialYearDates`).
 */
function storedFinancialYear(
  payload: LegalEntityWritePayload,
): Pick<ExtraFields, 'financialYearStartDate' | 'financialYearEndDate'> {
  const { start, end } = financialYearDates(payload.financialYearStartMonth);
  return { financialYearStartDate: start, financialYearEndDate: end };
}

function rememberExtras(level: string, id: number, fields: ExtraFields): void {
  if (extras.size === 0) extras = seedExtras();
  extras.set(extraKey(level, id), fields);
}

/* -- reading one row ------------------------------------------------------- */

export function mockGetEntity(id: number): LegalEntityRecord {
  const node = entities().find((entity) => entity.id === id);
  if (!node) throw notFound('entity', id);
  const extra = extrasFor('entity', id);
  return {
    id: node.id,
    organizationId: ORGANIZATION_ID,
    entityName: node.entityName,
    entityCode: node.entityCode,
    statusName: node.statusName ?? 'Active',
    integratedEntityCode: extra.integratedCode ?? null,
    financialYearStartDate: extra.financialYearStartDate ?? null,
    financialYearEndDate: extra.financialYearEndDate ?? null,
    entityLogo: extra.entityLogo ?? null,
    entityLogoName: extra.entityLogoName ?? null,
    description: extra.description ?? null,
  };
}

export function mockGetBusinessUnit(id: number): BusinessUnitRecord {
  const parent = entities().find((entity) => entity.businessUnits.some((unit) => unit.id === id));
  const node = parent?.businessUnits.find((unit) => unit.id === id);
  if (!parent || !node) throw notFound('business unit', id);
  const extra = extrasFor('businessUnit', id);
  return {
    id: node.id,
    legalEntityId: parent.id,
    name: node.name,
    businessUnitCode: node.businessUnitCode,
    integratedBusinessUnitCode: extra.integratedCode ?? null,
    description: extra.description ?? null,
    statusId: node.statusId ?? null,
    statusName: node.statusName ?? null,
  };
}

export function mockGetDivision(id: number): DivisionRecord {
  const parent = businessUnits().find((unit) => unit.divisions.some((division) => division.id === id));
  const node = parent?.divisions.find((division) => division.id === id);
  if (!parent || !node) throw notFound('division', id);
  const extra = extrasFor('division', id);
  return {
    id: node.id,
    businessUnitId: parent.id,
    name: node.name,
    divisionCode: node.divisionCode,
    integratedDivisionCode: extra.integratedCode ?? null,
    description: extra.description ?? null,
  };
}

export function mockGetDepartment(id: number): DepartmentRecord {
  const parent = divisions().find((division) =>
    division.departments.some((department) => department.id === id),
  );
  const node = parent?.departments.find((department) => department.id === id);
  if (!parent || !node) throw notFound('department', id);
  const extra = extrasFor('department', id);
  return {
    id: node.id,
    divisionId: parent.id,
    name: node.name,
    departmentCode: node.departmentCode,
    integratedDepartmentCode: extra.integratedCode ?? null,
    description: extra.description ?? null,
  };
}

/* -- entity ---------------------------------------------------------------- */

export function mockCreateEntity(payload: LegalEntityWritePayload): LegalEntityNodeRecord {
  // A create before any read still needs a tree to attach to.
  const root = current(tree?.organizationCode ?? 'ORG-001');
  const created: LegalEntityNodeRecord = {
    id: issueId(),
    entityName: payload.entityName,
    entityCode: payload.entityCode,
    statusName: payload.status ?? 'Active',
    businessUnits: [],
  };
  root.legalEntities.push(created);
  rememberExtras('entity', created.id, {
    integratedCode: payload.integratedEntityCode ?? undefined,
    description: payload.description ?? undefined,
    ...storedFinancialYear(payload),
    entityLogo: payload.entityLogo ?? undefined,
    entityLogoName: payload.entityLogoName ?? undefined,
  });
  return structuredClone(created);
}

export function mockUpdateEntity(
  id: number,
  payload: LegalEntityWritePayload,
): LegalEntityNodeRecord {
  const target = entities().find((entity) => entity.id === id);
  if (!target) throw notFound('entity', id);
  target.entityName = payload.entityName;
  target.entityCode = payload.entityCode;
  if (payload.status) target.statusName = payload.status;
  rememberExtras('entity', id, {
    integratedCode: payload.integratedEntityCode ?? undefined,
    description: payload.description ?? undefined,
    ...storedFinancialYear(payload),
    entityLogo: payload.entityLogo ?? undefined,
    entityLogoName: payload.entityLogoName ?? undefined,
  });
  return structuredClone(target);
}

export function mockDeleteEntity(id: number): void {
  if (tree) tree.legalEntities = tree.legalEntities.filter((entity) => entity.id !== id);
}

/* -- business unit --------------------------------------------------------- */

export function mockCreateBusinessUnit(
  payload: BusinessUnitWritePayload,
): BusinessUnitNodeRecord {
  const created: BusinessUnitNodeRecord = {
    id: issueId(),
    name: payload.name,
    businessUnitCode: payload.businessUnitCode,
    // Mirrors the service: an omitted status becomes Active, since the column is NOT NULL.
    statusId: 11,
    statusName: 'Active',
    divisions: [],
  };
  const parent = entities().find((entity) => entity.id === payload.legalEntityId);
  if (!parent) throw notFound('entity', payload.legalEntityId);
  parent.businessUnits.push(created);
  rememberExtras('businessUnit', created.id, {
    integratedCode: payload.integratedBusinessUnitCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(created);
}

export function mockUpdateBusinessUnit(
  id: number,
  payload: BusinessUnitWritePayload,
): BusinessUnitNodeRecord {
  const target = businessUnits().find((unit) => unit.id === id);
  if (!target) throw notFound('business unit', id);
  target.name = payload.name;
  target.businessUnitCode = payload.businessUnitCode;
  rememberExtras('businessUnit', id, {
    integratedCode: payload.integratedBusinessUnitCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(target);
}

export function mockDeleteBusinessUnit(id: number): void {
  entities().forEach((entity) => {
    entity.businessUnits = entity.businessUnits.filter((unit) => unit.id !== id);
  });
}

/* -- division -------------------------------------------------------------- */

export function mockCreateDivision(payload: DivisionWritePayload): DivisionNodeRecord {
  const created: DivisionNodeRecord = {
    id: issueId(),
    name: payload.name,
    divisionCode: payload.divisionCode,
    departments: [],
  };
  const parent = businessUnits().find((unit) => unit.id === payload.businessUnitId);
  if (!parent) throw notFound('business unit', payload.businessUnitId);
  parent.divisions.push(created);
  rememberExtras('division', created.id, {
    integratedCode: payload.integratedDivisionCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(created);
}

export function mockUpdateDivision(
  id: number,
  payload: DivisionWritePayload,
): DivisionNodeRecord {
  const target = divisions().find((division) => division.id === id);
  if (!target) throw notFound('division', id);
  target.name = payload.name;
  target.divisionCode = payload.divisionCode;
  rememberExtras('division', id, {
    integratedCode: payload.integratedDivisionCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(target);
}

export function mockDeleteDivision(id: number): void {
  businessUnits().forEach((unit) => {
    unit.divisions = unit.divisions.filter((division) => division.id !== id);
  });
}

/* -- department ------------------------------------------------------------ */

export function mockCreateDepartment(payload: DepartmentWritePayload): DepartmentNodeRecord {
  const created: DepartmentNodeRecord = {
    id: issueId(),
    name: payload.name,
    departmentCode: payload.departmentCode,
  };
  const parent = divisions().find((division) => division.id === payload.divisionId);
  if (!parent) throw notFound('division', payload.divisionId);
  parent.departments.push(created);
  rememberExtras('department', created.id, {
    integratedCode: payload.integratedDepartmentCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(created);
}

export function mockUpdateDepartment(
  id: number,
  payload: DepartmentWritePayload,
): DepartmentNodeRecord {
  const target = departments().find((department) => department.id === id);
  if (!target) throw notFound('department', id);
  target.name = payload.name;
  target.departmentCode = payload.departmentCode;
  rememberExtras('department', id, {
    integratedCode: payload.integratedDepartmentCode ?? undefined,
    description: payload.description ?? undefined,
  });
  return structuredClone(target);
}

export function mockDeleteDepartment(id: number): void {
  divisions().forEach((division) => {
    division.departments = division.departments.filter((department) => department.id !== id);
  });
}
